import { DIALOG_COLORS, Badge, Button } from "../../../components/ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { brand } from "../../../config/theme";
import Modal from "../../../components/base/Modal";
import { buildThemeClasses } from "../theme";
import {
  fetchBookings,
  fetchBlocks,
  getAvailability,
  createBooking,
  updateBooking,
  deleteBooking,
  createBlock,
  deleteBlock,
  tzParts,
  fmtTime,
  fmtDateLong,
  dateKey,
  BOOKING_STATUS_META,
  WEEKDAYS_ES,
  MONTHS_ES,
  type Booking,
  type TimeBlock,
  type Availability,
  type BookingStatus,
} from "../services/bookingsService";
import {
  fetchAgents,
  getAgentGoogleAuthUrl,
  getAgentGoogleStatus,
  disconnectAgentGoogle,
  fetchAgentGoogleEvents,
  type Agent,
  type AgentGoogleStatus,
} from "../services/agentsService";
import { fetchLeads, type Lead } from "../services/leadsService";
import { isPlatformAdminUser } from "../../../hooks/useIsReseller";
import { useUserStore } from "../../../store/userStore";
import { findMyAgent } from "../../../pages/profile/GoogleCalendarIntegration";
import { matchesLeadQuery, phoneChannelOf, phoneChannels } from "../services/leadPhone";
import {
  MonthGrid,
  TimeGrid,
  keyToUTC,
  utcToKey,
  addDaysKey,
  weekdayOfKey,
  weekKeys,
  formatMinInput,
  addMinutesToInput,
  type View,
  type CalEvent,
} from "../components/CalendarGrids";

// ===========================================================================
// Reuniones — per-agent meeting calendar. Each human agent connects their OWN
// Google Calendar (OAuth); the meetings/blocks created here are written to that
// agent's calendar (with a Meet link) and shown in Mes / Semana / Hoy views.
// Meetings link to a CRM lead. Pre-existing Google events show as read-only busy.
// ===========================================================================

/** Perfil → Integraciones: donde cada vendedor conecta SU Google Calendar. */
export const PROFILE_INTEGRATIONS_PATH = "/profile?tab=integrations";

// ---------------------------------------------------------------------------
// Conexión de Google Calendar del agente elegido.
//   • Administrador de plataforma (owner o admin reseller): conecta o desconecta
//     el calendario de CUALQUIER vendedor desde aquí.
//   • Cualquier otro: sólo lee el estado (la pastilla de al lado) y se le lleva
//     a su perfil, que es donde cada quien conecta el suyo. No se le ofrece
//     conectar el de otra persona.
// El botón oculto no es la seguridad: la API responde 403 a quien no puede.
// ---------------------------------------------------------------------------
export function GoogleConnectionControls({
  canManage,
  isOwnAgent,
  status,
  connecting,
  noAgents,
  onConnect,
  onDisconnect,
}: {
  canManage: boolean;
  isOwnAgent: boolean;
  status: AgentGoogleStatus | null;
  connecting: boolean;
  noAgents: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}) {
  const { t } = useTranslation("common");

  if (!canManage) {
    return (
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm" data-testid="google-connect-from-profile">
        {!isOwnAgent && !noAgents && (
          <span className="text-xs text-content-muted">
            <i className="ri-information-line mr-1" />
            {t(
              "profile.integrations.google.meetings.eachSellerOwn",
              "Each sales agent connects their own Google Calendar from their profile.",
            )}
          </span>
        )}
        {/* Mismo look que la variante `link` de Button, pero es navegación: va con <Link>. */}
        <Link
          to={PROFILE_INTEGRATIONS_PATH}
          className="inline-flex items-center gap-1 font-medium text-primary-500 underline-offset-2 hover:underline"
        >
          <i className="ri-external-link-line" />
          {isOwnAgent && status?.connected
            ? t("profile.integrations.google.meetings.manageFromProfile", "Manage your Google Calendar from your profile")
            : t("profile.integrations.google.meetings.connectFromProfile", "Connect your Google Calendar from your profile")}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {status && !status.oauthConfigured && (
        <span className="text-xs text-warning">
          <i className="ri-error-warning-line mr-1" /> Configura el cliente OAuth de Google en el servidor
        </span>
      )}
      {status?.connected ? (
        <Button variant="outline" icon={<i className="ri-link-unlink" />} onClick={onDisconnect}>
          Desconectar
        </Button>
      ) : (
        <Button
          icon={<i className="ri-google-fill" />}
          loading={connecting}
          disabled={noAgents || (status ? status.oauthConfigured === false : false)}
          onClick={onConnect}
        >
          Conectar Google Calendar
        </Button>
      )}
    </div>
  );
}

export default function ReunionesLive() {
  const tc = buildThemeClasses();
  // Conectar el calendario de OTRO vendedor es de un administrador de plataforma
  // (misma regla que la API). Los demás lo conectan desde su perfil.
  const canManageCalendars = isPlatformAdminUser();
  const myEmail = useUserStore((state) => state.user?.email);

  const [agents, setAgents] = useState<Agent[]>([]);
  const [agentId, setAgentId] = useState<string>("");
  const [status, setStatus] = useState<AgentGoogleStatus | null>(null);
  const [connecting, setConnecting] = useState(false);

  const [availability, setAvailability] = useState<Availability | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [blocks, setBlocks] = useState<TimeBlock[]>([]);
  const [external, setExternal] = useState<CalEvent[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<View>("month");
  const [refKey, setRefKey] = useState<string>(() => utcToKey(new Date()));
  const [selected, setSelected] = useState<CalEvent | null>(null);
  const [composer, setComposer] = useState<{ kind: "booking" | "block"; startKey: string; startMin: number } | null>(null);

  const pollRef = useRef<number | null>(null);

  const tz = availability?.timezone || "America/La_Paz";
  const offset = availability?.utcOffset || "-04:00";
  const slotMinutes = availability?.slotMinutes || 30;
  const todayKey = useMemo(() => dateKey(new Date(), tz), [tz]);
  const selectedAgent = useMemo(() => agents.find((a) => a.id === agentId) || null, [agents, agentId]);
  const myAgentId = useMemo(() => findMyAgent(agents, myEmail)?.id ?? null, [agents, myEmail]);

  // Load human agents + availability + lead roster once.
  const loadBase = useCallback(async () => {
    try {
      const [page, av] = await Promise.all([
        fetchAgents({ type: "human", limit: 300 }),
        getAvailability(),
      ]);
      if (av) setAvailability(av);
      const humans = page.data;
      setAgents(humans);
      // Un vendedor abre en SU agenda; un administrador, en la primera de la lista.
      const mine = canManageCalendars ? null : findMyAgent(humans, myEmail);
      setAgentId((prev) => prev || mine?.id || humans[0]?.id || "");
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || "No se pudieron cargar los agentes");
    }
    // Lead roster for the meeting picker (best-effort).
    try {
      const lp = await fetchLeads({ limit: 200 });
      setLeads(lp.data);
    } catch {
      /* keep last known leads */
    }
  }, [canManageCalendars, myEmail]);

  // Load the selected agent's meetings/blocks/status/external events.
  const loadAgent = useCallback(
    async (id: string, silent = false) => {
      if (!id) {
        setBookings([]);
        setBlocks([]);
        setExternal([]);
        setStatus(null);
        setLoading(false);
        return;
      }
      if (!silent) setLoading(true);
      setError(null);
      try {
        const st = await getAgentGoogleStatus(id);
        setStatus(st);
        const [bk, bl] = await Promise.all([fetchBookings({ agentId: id }), fetchBlocks({ agentId: id })]);
        setBookings(bk);
        setBlocks(bl);

        // Pre-existing Google events (read-only busy), de-duped against the
        // events we already own here (by googleEventId).
        if (st.connected) {
          const range = monthRange(refKey);
          try {
            const owned = new Set(
              [...bk.map((b) => b.googleEventId), ...bl.map((b) => b.googleEventId)].filter(Boolean) as string[],
            );
            const gevents = await fetchAgentGoogleEvents(id, range.from, range.to);
            const ext: CalEvent[] = [];
            for (const ev of gevents) {
              if (!ev.start || !ev.end || owned.has(ev.id)) continue;
              const s = tzParts(new Date(ev.start), tz);
              const e = tzParts(new Date(ev.end), tz);
              ext.push({
                id: `gc_${ev.id}`,
                kind: "external",
                title: ev.summary,
                startKey: s.dateKey,
                startMin: ev.allDay ? 0 : s.hour * 60 + s.minute,
                endMin: ev.allDay ? 24 * 60 : e.dateKey === s.dateKey ? e.hour * 60 + e.minute : 24 * 60,
                htmlLink: ev.htmlLink,
              });
            }
            setExternal(ext);
          } catch {
            setExternal([]);
          }
        } else {
          setExternal([]);
        }
      } catch (e) {
        const err = e as { response?: { data?: { message?: string } }; message?: string };
        setError(err.response?.data?.message || err.message || "No se pudo cargar la agenda del agente");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [refKey, tz],
  );

  useEffect(() => {
    loadBase();
  }, [loadBase]);

  useEffect(() => {
    loadAgent(agentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  // Reload external events when the visible month changes (own events come from
  // our DB and are already loaded; Google range tracks the calendar).
  useEffect(() => {
    if (agentId && status?.connected) loadAgent(agentId, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refKey.slice(0, 7)]);

  // --- Google connect (OAuth popup) -----------------------------------------
  const connect = async () => {
    if (!agentId || !canManageCalendars) return;
    setConnecting(true);
    try {
      const url = await getAgentGoogleAuthUrl(agentId);
      if (!url) throw new Error("No se pudo generar la URL de conexión");
      const popup = window.open(url, "crm-google-oauth", "width=520,height=680");

      // Resolve when the popup posts back, or stop after a timeout.
      const finish = async () => {
        window.removeEventListener("message", onMsg);
        if (pollRef.current) window.clearInterval(pollRef.current);
        setConnecting(false);
        await loadAgent(agentId, true);
        const st = await getAgentGoogleStatus(agentId).catch(() => null);
        if (st?.connected) {
          Swal.fire({
            icon: "success",
            title: "Google Calendar conectado",
            text: st.email ? `Conectado como ${st.email}.` : "Conexión exitosa.",
            confirmButtonColor: brand.primary,
          });
        }
      };
      const onMsg = (ev: MessageEvent) => {
        if (ev.data && ev.data.source === "crm-google-oauth") finish();
      };
      window.addEventListener("message", onMsg);

      // Fallback: poll status + detect popup close (cross-origin safe).
      let ticks = 0;
      pollRef.current = window.setInterval(async () => {
        ticks += 1;
        const st = await getAgentGoogleStatus(agentId).catch(() => null);
        if (st?.connected || (popup && popup.closed) || ticks > 48) {
          if (st) setStatus(st);
          finish();
        }
      }, 2500);
    } catch (e) {
      setConnecting(false);
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({
        icon: "error",
        title: "No se pudo conectar",
        text: err.response?.data?.message || err.message || "Intenta de nuevo",
        confirmButtonColor: brand.primary,
      });
    }
  };

  useEffect(() => () => { if (pollRef.current) window.clearInterval(pollRef.current); }, []);

  const disconnect = async () => {
    if (!agentId || !canManageCalendars) return;
    const res = await Swal.fire({
      icon: "warning",
      title: "Desconectar Google Calendar",
      text: "Las reuniones existentes se conservan, pero las nuevas no se crearán en este calendario.",
      showCancelButton: true,
      confirmButtonText: "Desconectar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!res.isConfirmed) return;
    try {
      await disconnectAgentGoogle(agentId);
      await loadAgent(agentId, true);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      Swal.fire({ icon: "error", title: "Error", text: err.response?.data?.message || err.message || "No se pudo desconectar", confirmButtonColor: brand.primary });
    }
  };

  // --- unified events --------------------------------------------------------
  const events = useMemo<CalEvent[]>(() => {
    const out: CalEvent[] = [];
    for (const b of bookings) {
      if (!b.start || !b.end) continue;
      const s = tzParts(new Date(b.start), tz);
      const e = tzParts(new Date(b.end), tz);
      out.push({
        id: b.id,
        kind: "booking",
        title: b.title || b.name || "Reunión",
        startKey: s.dateKey,
        startMin: s.hour * 60 + s.minute,
        endMin: e.dateKey === s.dateKey ? e.hour * 60 + e.minute : 24 * 60,
        status: b.status,
        booking: b,
      });
    }
    for (const bl of blocks) {
      if (!bl.start || !bl.end) continue;
      const s = tzParts(new Date(bl.start), tz);
      const e = tzParts(new Date(bl.end), tz);
      out.push({
        id: bl.id,
        kind: "block",
        title: bl.title || "Bloqueo",
        startKey: s.dateKey,
        startMin: bl.allDay ? 0 : s.hour * 60 + s.minute,
        endMin: bl.allDay ? 24 * 60 : e.dateKey === s.dateKey ? e.hour * 60 + e.minute : 24 * 60,
        block: bl,
      });
    }
    out.push(...external);
    return out;
  }, [bookings, blocks, external, tz]);

  const eventsByDay = useMemo(() => {
    const map: Record<string, CalEvent[]> = {};
    for (const ev of events) (map[ev.startKey] ||= []).push(ev);
    for (const k of Object.keys(map)) map[k].sort((a, b) => a.startMin - b.startMin);
    return map;
  }, [events]);

  const [dayStartH, dayEndH] = useMemo(() => {
    let minS = 8 * 60;
    let maxE = 18 * 60;
    const weekly = availability?.weekly || {};
    for (const wins of Object.values(weekly)) {
      for (const w of wins) {
        const [sh, sm] = w.start.split(":").map(Number);
        const [eh, em] = w.end.split(":").map(Number);
        minS = Math.min(minS, sh * 60 + sm);
        maxE = Math.max(maxE, eh * 60 + em);
      }
    }
    for (const ev of events) {
      if (ev.kind === "block" && ev.block?.allDay) continue;
      minS = Math.min(minS, ev.startMin);
      maxE = Math.max(maxE, ev.endMin);
    }
    return [Math.max(0, Math.floor(minS / 60)), Math.min(24, Math.ceil(maxE / 60))];
  }, [availability, events]);

  const move = (dir: number) => {
    if (view === "month") {
      const [y, m] = refKey.split("-").map(Number);
      setRefKey(utcToKey(new Date(Date.UTC(y, m - 1 + dir, 1, 12))));
    } else if (view === "week") {
      setRefKey(addDaysKey(refKey, dir * 7));
    } else {
      setRefKey(addDaysKey(refKey, dir));
    }
  };
  const goToday = () => {
    setRefKey(utcToKey(new Date()));
    setView("day");
  };

  const headerLabel = useMemo(() => {
    const [y, m] = refKey.split("-").map(Number);
    if (view === "month") return `${MONTHS_ES[m - 1]} ${y}`;
    if (view === "day") {
      const p = keyToUTC(refKey);
      return `${WEEKDAYS_ES[p.getUTCDay()]} ${p.getUTCDate()} ${MONTHS_ES[p.getUTCMonth()]} ${p.getUTCFullYear()}`;
    }
    const wk = weekKeys(refKey);
    const a = keyToUTC(wk[0]);
    const b = keyToUTC(wk[6]);
    return `${a.getUTCDate()} ${MONTHS_ES[a.getUTCMonth()].slice(0, 3)} – ${b.getUTCDate()} ${MONTHS_ES[b.getUTCMonth()].slice(0, 3)} ${b.getUTCFullYear()}`;
  }, [refKey, view]);

  const isWithinAvailability = useCallback(
    (key: string, minute: number) => {
      const wins = availability?.weekly?.[String(weekdayOfKey(key))] || [];
      return wins.some((w) => {
        const [sh, sm] = w.start.split(":").map(Number);
        const [eh, em] = w.end.split(":").map(Number);
        return minute >= sh * 60 + sm && minute < eh * 60 + em;
      });
    },
    [availability],
  );

  const stats = useMemo(() => {
    const now = new Date();
    const upcoming = bookings.filter((b) => b.status !== "cancelled" && new Date(b.start) >= now);
    return { upcoming: upcoming.length, blocks: blocks.length };
  }, [bookings, blocks]);

  const openSlot = (kind: "booking" | "block", startKey: string, startMin: number) =>
    setComposer({ kind, startKey, startMin });

  const noAgents = agents.length === 0;

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-calendar-event-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Reuniones</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                Agenda de cada agente con su Google Calendar · zona horaria {tz}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => openSlot("block", todayKey, 9 * 60)}
              disabled={noAgents}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${tc.border} ${tc.cardBg} ${tc.text} hover:border-primary-500 disabled:opacity-50`}
            >
              <i className="ri-lock-line" /> Bloquear tiempo
            </button>
            <button
              onClick={() => openSlot("booking", todayKey, 9 * 60)}
              disabled={noAgents}
              className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary transition-colors hover:bg-primary-400 disabled:opacity-50"
            >
              <i className="ri-add-line" /> Nueva reunión
            </button>
          </div>
        </div>

        {/* Agent selector + Google connection */}
        <div className={`mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3 ${tc.cardBorder} ${tc.cardBg}`}>
          <div className="flex items-center gap-2">
            <label className={`text-xs font-medium ${tc.textSecondary}`}>Agente</label>
            <select
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              disabled={noAgents}
              className="rounded-lg border px-3 py-2 text-sm outline-none border-line bg-app text-content disabled:opacity-50"
            >
              {noAgents ? (
                <option value="">Sin agentes humanos</option>
              ) : (
                agents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                    {a.role ? ` · ${a.role}` : ""}
                  </option>
                ))
              )}
            </select>
            {status && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                  status.connected ? "bg-emerald-500/15 text-emerald-500" : "bg-gray-400/15 text-gray-500"
                }`}
                title={status.email || undefined}
              >
                <i className={status.connected ? "ri-google-fill" : "ri-google-line"} />
                {status.connected ? `Conectado${status.email ? ` · ${status.email}` : ""}` : "No conectado"}
              </span>
            )}
          </div>

          <GoogleConnectionControls
            canManage={canManageCalendars}
            isOwnAgent={Boolean(agentId) && agentId === myAgentId}
            status={status}
            connecting={connecting}
            noAgents={noAgents}
            onConnect={connect}
            onDisconnect={disconnect}
          />
        </div>

        {/* Stat chips */}
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-2">
          <Stat tc={tc} icon="ri-calendar-check-line" label="Próximas reuniones" value={stats.upcoming} />
          <Stat tc={tc} icon="ri-lock-line" label="Bloqueos" value={stats.blocks} tone="text-gray-400" />
        </div>

        {/* Toolbar */}
        <div className={`mb-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border p-2 ${tc.cardBorder} ${tc.cardBg}`}>
          <div className="flex items-center gap-1">
            <button onClick={() => move(-1)} className={`rounded-lg p-2 ${tc.buttonHover} ${tc.text}`} title="Anterior">
              <i className="ri-arrow-left-s-line" />
            </button>
            <button onClick={goToday} className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${tc.border} ${tc.text} hover:border-primary-500`}>
              Hoy
            </button>
            <button onClick={() => move(1)} className={`rounded-lg p-2 ${tc.buttonHover} ${tc.text}`} title="Siguiente">
              <i className="ri-arrow-right-s-line" />
            </button>
            <span className={`ml-2 text-sm font-semibold capitalize ${tc.text}`}>{headerLabel}</span>
          </div>
          <div className="flex items-center gap-1 rounded-lg border p-0.5">
            {(["month", "week", "day"] as View[]).map((v) => (
              <button
                key={v}
                onClick={() => {
                  if (v === "day") setRefKey(utcToKey(new Date()));
                  setView(v);
                }}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  view === v ? "bg-primary-500 text-white" : `${tc.textSecondary} hover:text-primary-500`
                }`}
              >
                {v === "month" ? "Mes" : v === "week" ? "Semana" : "Hoy"}
              </button>
            ))}
          </div>
        </div>

        {/* Calendar body */}
        <div className={`rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`}>
          {noAgents ? (
            <div className={`p-12 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-user-line mb-2 block text-3xl" />
              No hay agentes humanos. Crea uno en Configuración → Agentes → Humanos.
            </div>
          ) : loading ? (
            <div className={`p-12 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-loader-4-line mr-2 animate-spin" /> Cargando agenda...
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <i className="ri-error-warning-line mb-2 text-3xl text-red-500" />
              <p className="text-sm text-red-500">{error}</p>
              <button onClick={() => loadAgent(agentId)} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-on-primary hover:bg-primary-400">
                Reintentar
              </button>
            </div>
          ) : view === "month" ? (
            <MonthGrid
              refKey={refKey}
              todayKey={todayKey}
              eventsByDay={eventsByDay}
              tc={tc}
              onPickDay={(k) => {
                setRefKey(k);
                setView("day");
              }}
              onSelect={setSelected}
            />
          ) : (
            <TimeGrid
              keys={view === "week" ? weekKeys(refKey) : [refKey]}
              dayStartH={dayStartH}
              dayEndH={dayEndH}
              todayKey={todayKey}
              eventsByDay={eventsByDay}
              isWithinAvailability={isWithinAvailability}
              tc={tc}
              onEmptyClick={(k, min) => openSlot("booking", k, min)}
              onSelect={setSelected}
            />
          )}
        </div>

        <p className={`mt-3 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          Conecta el Google Calendar del agente para que sus reuniones y bloqueos se creen ahí (con enlace de Meet).
          Los eventos existentes de Google se muestran como ocupado (📅, solo lectura).
        </p>
      </div>

      {selected && (
        <EventDetail
          event={selected}
          tc={tc}
          tz={tz}
          onClose={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
            loadAgent(agentId, true);
          }}
        />
      )}

      {composer && (
        <Composer
          kind={composer.kind}
          startKey={composer.startKey}
          startMin={composer.startMin}
          slotMinutes={slotMinutes}
          offset={offset}
          tz={tz}
          agentId={agentId}
          leads={leads}
          tc={tc}
          onClose={() => setComposer(null)}
          onCreated={() => {
            setComposer(null);
            loadAgent(agentId, true);
          }}
        />
      )}
    </div>
  );
}

/** Calendar month [from,to] ISO range around a refKey (pads ±1 week). */
function monthRange(refKey: string) {
  const [y, m] = refKey.split("-").map(Number);
  const from = new Date(Date.UTC(y, m - 1, 1) - 7 * 86400000).toISOString();
  const to = new Date(Date.UTC(y, m, 1) + 7 * 86400000).toISOString();
  return { from, to };
}

// ---------------------------------------------------------------------------
// Event detail — reschedule via status actions / delete / Meet link.
// ---------------------------------------------------------------------------
function EventDetail({
  event,
  tc,
  tz,
  onClose,
  onChanged,
}: {
  event: CalEvent;
  tc: ReturnType<typeof buildThemeClasses>;
  tz: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);

  if (event.kind === "external") {
    return (
      <Modal
        title="Evento de Google Calendar"
        themeClasses={tc}
        onClose={onClose}
        width="max-w-lg"
        content={
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2">
              <i className="ri-google-fill text-lg text-sky-500" />
              <span className={`font-semibold ${tc.text}`}>{event.title}</span>
            </div>
            <p className={tc.textSecondary}>Evento existente en el calendario del agente (solo lectura).</p>
            {event.htmlLink && (
              <a href={event.htmlLink} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-sky-500/15 px-3 py-2 text-sm font-medium text-sky-600 hover:bg-sky-500/25">
                <i className="ri-external-link-line" /> Abrir en Google Calendar
              </a>
            )}
          </div>
        }
      />
    );
  }

  const setStatus = async (status: BookingStatus) => {
    if (!event.booking) return;
    setBusy(true);
    try {
      await updateBooking(event.booking.id, { status });
      onChanged();
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo actualizar", confirmButtonColor: brand.primary });
      setBusy(false);
    }
  };

  const remove = async () => {
    const res = await Swal.fire({
      icon: "warning",
      title: event.kind === "block" ? "Eliminar bloqueo" : "Eliminar reunión",
      text: "Esta acción no se puede deshacer (también se quita de Google Calendar).",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: DIALOG_COLORS.danger,
    });
    if (!res.isConfirmed) return;
    setBusy(true);
    try {
      if (event.kind === "block") await deleteBlock(event.id);
      else await deleteBooking(event.id);
      onChanged();
    } catch {
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo eliminar", confirmButtonColor: brand.primary });
      setBusy(false);
    }
  };

  const b = event.booking;
  return (
    <Modal
      title={event.kind === "block" ? "Bloqueo de tiempo" : "Detalle de reunión"}
      themeClasses={tc}
      onClose={onClose}
      width="max-w-lg"
      content={
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <i className={`${event.kind === "block" ? "ri-lock-line text-gray-400" : "ri-calendar-check-line text-primary-500"} text-lg`} />
            <span className={`font-semibold ${tc.text}`}>{event.title}</span>
          </div>
          <p className={tc.textSecondary}>
            <i className="ri-time-line mr-1" />
            {fmtDateLong((b ?? event.block)!.start, tz)} · {fmtTime((b ?? event.block)!.start, tz)}–{fmtTime((b ?? event.block)!.end, tz)}
          </p>

          {b && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {b.type === "demo" && (
                  <Badge tone="success">
                    <i className="ri-presentation-line" /> Demo
                  </Badge>
                )}
                {b.status && (
                  <Badge tone={BOOKING_STATUS_META[b.status].tone}>
                    {BOOKING_STATUS_META[b.status].label}
                  </Badge>
                )}
              </div>
              <div className={`grid grid-cols-2 gap-2 rounded-xl border p-3 ${tc.border}`}>
                <Field tc={tc} label="Contacto" value={b.name} />
                <Field tc={tc} label="Empresa" value={b.company} />
                <Field tc={tc} label="Correo" value={b.email} />
                <Field tc={tc} label="WhatsApp" value={b.whatsapp} />
                {/* Sólo cuando el prospecto dijo que su número NO tiene WhatsApp. */}
                {b.phone && <Field tc={tc} label="Teléfono (sin WhatsApp)" value={b.phone} />}
              </div>
              {b.notes && <p className={`text-sm ${tc.text}`}>{b.notes}</p>}
              {b.meetingUrl && (
                <a href={b.meetingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-emerald-500/15 px-3 py-2 text-sm font-medium text-emerald-600 hover:bg-emerald-500/25">
                  <i className="ri-vidicon-line" /> Unirse a la reunión
                </a>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                {b.status !== "confirmed" && (
                  <button disabled={busy} onClick={() => setStatus("confirmed")} className="rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-400 disabled:opacity-60">Confirmar</button>
                )}
                {b.status !== "completed" && (
                  <button disabled={busy} onClick={() => setStatus("completed")} className="rounded-lg bg-sky-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-sky-400 disabled:opacity-60">Completada</button>
                )}
                {b.status !== "no_show" && (
                  <button disabled={busy} onClick={() => setStatus("no_show")} className="rounded-lg bg-red-500/90 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-500 disabled:opacity-60">No asistió</button>
                )}
                {b.status !== "cancelled" && (
                  <button disabled={busy} onClick={() => setStatus("cancelled")} className="rounded-lg border px-3 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-500/10 disabled:opacity-60">Cancelar</button>
                )}
              </div>
            </>
          )}

          {event.block?.reason && <p className={`text-sm ${tc.text}`}>{event.block.reason}</p>}

          <div className="flex justify-end border-t pt-3">
            <button disabled={busy} onClick={remove} className="rounded-lg px-3 py-1.5 text-xs font-medium text-red-500 hover:bg-red-500/10 disabled:opacity-60">
              <i className="ri-delete-bin-line mr-1" /> Eliminar
            </button>
          </div>
        </div>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Composer — create a meeting (lead-linked) or a time block on the agent's cal.
// ---------------------------------------------------------------------------
// Exported for unit tests (see ReunionesLive.test.tsx).
export function Composer({
  kind,
  startKey,
  startMin,
  slotMinutes,
  offset,
  tz,
  agentId,
  leads,
  tc,
  onClose,
  onCreated,
}: {
  kind: "booking" | "block";
  startKey: string;
  startMin: number;
  slotMinutes: number;
  offset: string;
  tz: string;
  agentId: string;
  leads: Lead[];
  tc: ReturnType<typeof buildThemeClasses>;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [mode, setMode] = useState<"booking" | "block">(kind);
  const [date, setDate] = useState(startKey);
  const [time, setTime] = useState(formatMinInput(startMin));
  const [duration, setDuration] = useState(mode === "block" ? 60 : slotMinutes);
  const [title, setTitle] = useState("");
  const [leadId, setLeadId] = useState<string>("");
  const [leadQuery, setLeadQuery] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  // ONE number + "¿tiene WhatsApp?", exactly like the landing form: the answer
  // decides whether it is stored as `whatsapp` or as `phone`.
  const [phoneNumber, setPhoneNumber] = useState("");
  const [hasWhatsapp, setHasWhatsapp] = useState(true);
  const [company, setCompany] = useState("");
  const [notes, setNotes] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const buildISO = (t: string) => `${date}T${t}:00${offset}`;

  const leadMatches = useMemo(() => {
    const q = leadQuery.trim();
    if (!q) return leads.slice(0, 8);
    // Same matcher as the Leads inbox → a phone-only lead is findable by number.
    return leads.filter((l) => matchesLeadQuery(l, q)).slice(0, 8);
  }, [leads, leadQuery]);

  const pickLead = (l: Lead) => {
    setLeadId(l.id);
    setLeadQuery(l.name || l.company || l.email || "");
    setName(l.name || "");
    setEmail(l.email || "");
    const channel = phoneChannelOf(l);
    setPhoneNumber(channel.value);
    setHasWhatsapp(channel.hasWhatsapp);
    setCompany(l.company || "");
    if (!title) setTitle(`Reunión — ${l.name || l.company || "Lead"}`);
  };

  const submit = async () => {
    setErr(null);
    setBusy(true);
    try {
      if (mode === "block") {
        const start = allDay ? `${date}T00:00:00${offset}` : buildISO(time);
        const end = allDay ? `${date}T23:59:00${offset}` : buildISO(addMinutesToInput(time, duration));
        await createBlock({ title: title || "Bloqueo", reason: notes, start, end, allDay, agentId });
      } else {
        if (!name && !email) {
          setErr("Indica un lead o al menos un nombre o correo.");
          setBusy(false);
          return;
        }
        const start = buildISO(time);
        const end = buildISO(addMinutesToInput(time, duration));
        await createBooking({
          title: title || `Reunión — ${name || email}`,
          type: "meeting",
          name,
          email,
          ...phoneChannels(phoneNumber, hasWhatsapp),
          company,
          notes,
          start,
          end,
          status: "confirmed",
          timezone: tz,
          agentId,
          leadId: leadId || undefined,
        });
      }
      onCreated();
    } catch (e) {
      const error = e as { response?: { data?: { data?: { message?: string }; message?: string } }; message?: string };
      setErr(error.response?.data?.data?.message || error.response?.data?.message || error.message || "No se pudo guardar");
      setBusy(false);
    }
  };

  return (
    <Modal
      title={mode === "block" ? "Bloquear tiempo" : "Nueva reunión"}
      themeClasses={tc}
      onClose={onClose}
      width="max-w-lg"
      content={
        <div className="space-y-3">
          <div className="flex gap-1 rounded-lg border p-0.5">
            {(["booking", "block"] as const).map((m) => (
              <button
                key={m}
                onClick={() => {
                  setMode(m);
                  setDuration(m === "block" ? 60 : slotMinutes);
                }}
                className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  mode === m ? "bg-primary-500 text-white" : `${tc.textSecondary} hover:text-primary-500`
                }`}
              >
                {m === "booking" ? "Reunión" : "Bloqueo"}
              </button>
            ))}
          </div>

          {err && <p className="rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-500">{err}</p>}

          {mode === "booking" && (
            <div>
              <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Lead</label>
              <input
                value={leadQuery}
                onChange={(e) => {
                  setLeadQuery(e.target.value);
                  setLeadId("");
                }}
                placeholder="Buscar lead por nombre, empresa, correo o teléfono..."
                className={`w-full rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`}
              />
              {leadQuery && !leadId && leadMatches.length > 0 && (
                <div className={`mt-1 max-h-40 overflow-y-auto rounded-lg border ${tc.border} bg-card`}>
                  {leadMatches.map((l) => (
                    <button
                      key={l.id}
                      onClick={() => pickLead(l)}
                      className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-primary-500/10 ${tc.text}`}
                    >
                      {l.name || "Sin nombre"}
                      <span className={`ml-1 text-xs ${tc.textSecondary}`}>{[l.company, l.email].filter(Boolean).join(" · ")}</span>
                    </button>
                  ))}
                </div>
              )}
              {leadId && (
                <p className="mt-1 text-xs text-emerald-500">
                  <i className="ri-checkbox-circle-line mr-1" /> Lead vinculado
                </p>
              )}
            </div>
          )}

          <Input tc={tc} label="Título" value={title} onChange={setTitle} placeholder={mode === "block" ? "Tiempo personal" : "Demo de producto"} />

          {mode === "booking" && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Input tc={tc} label="Nombre" value={name} onChange={setName} />
                <Input tc={tc} label="Empresa" value={company} onChange={setCompany} />
                <Input tc={tc} label="Correo" value={email} onChange={setEmail} type="email" />
                <Input tc={tc} label="Teléfono" value={phoneNumber} onChange={setPhoneNumber} placeholder="+52 55 1234 5678" />
              </div>
              <label className={`flex items-center gap-2 text-sm ${tc.text}`}>
                <input type="checkbox" checked={hasWhatsapp} onChange={(e) => setHasWhatsapp(e.target.checked)} />
                Este número tiene WhatsApp
              </label>
            </>
          )}

          <div className="grid grid-cols-3 gap-2">
            <Input tc={tc} label="Fecha" value={date} onChange={setDate} type="date" />
            {!allDay && <Input tc={tc} label="Hora" value={time} onChange={setTime} type="time" />}
            {!allDay && (
              <div>
                <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Duración</label>
                <select
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  className={`w-full rounded-lg border px-2 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`}
                >
                  {[15, 30, 45, 60, 90, 120].map((d) => (
                    <option key={d} value={d}>{d} min</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {mode === "block" && (
            <label className={`flex items-center gap-2 text-sm ${tc.text}`}>
              <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
              Todo el día
            </label>
          )}

          <div>
            <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>{mode === "block" ? "Motivo" : "Notas"}</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={`w-full rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`}
            />
          </div>

          <div className="flex justify-end gap-2 border-t pt-3">
            <button onClick={onClose} className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.text}`}>Cancelar</button>
            <button onClick={submit} disabled={busy} className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-400 disabled:opacity-60">
              {busy ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </div>
      }
    />
  );
}

// --- small presentational helpers ------------------------------------------
function Stat({ tc, icon, label, value, tone }: { tc: ReturnType<typeof buildThemeClasses>; icon: string; label: string; value: number; tone?: string }) {
  return (
    <div className={`flex items-center gap-3 rounded-xl border p-3 ${tc.cardBorder} ${tc.cardBg}`}>
      <span className={`flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500/10 ${tone ?? "text-primary-500"}`}>
        <i className={icon} />
      </span>
      <div>
        <p className={`text-lg font-bold ${tc.text}`}>{value}</p>
        <p className={`text-xs ${tc.textSecondary}`}>{label}</p>
      </div>
    </div>
  );
}

function Field({ tc, label, value }: { tc: ReturnType<typeof buildThemeClasses>; label: string; value: string | null | undefined }) {
  return (
    <div>
      <p className={`text-[11px] ${tc.textSecondary}`}>{label}</p>
      <p className={`text-sm ${tc.text}`}>{value || "—"}</p>
    </div>
  );
}

function Input({
  tc,
  label,
  value,
  onChange,
  type = "text",
  placeholder,
}: {
  tc: ReturnType<typeof buildThemeClasses>;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`}
      />
    </div>
  );
}
